'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/common/button';
import { Pagination as PaginationRoot, PaginationContent, PaginationItem } from '@/components/common/pagination';
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  searchParams: Record<string, string | string[] | undefined>;
}
function buildHref(pathname: string, searchParams: Record<string, string | string[] | undefined>, page: number) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(searchParams)) {
    if (!value) continue;
    if (Array.isArray(value)) {
      for (const v of value) {
        if (v) params.append(key, v);
      }
    } else {
      params.set(key, value);
    }
  }
  if (page <= 1) params.delete('page');else params.set('page', page.toString());
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}
export function Pagination(t0) {
  const $ = _c(38);
  const {
    currentPage,
    totalPages,
    searchParams
  } = t0;
  const pathname = usePathname();
  const pageFromUrlRaw = searchParams.page;
  const pageFromUrlString = typeof pageFromUrlRaw === "string" ? pageFromUrlRaw : undefined;
  const pageFromUrl = pageFromUrlString ? parseInt(pageFromUrlString, 10) : null;
  const resolvedCurrentPage = pageFromUrl && pageFromUrl >= 1 && pageFromUrl <= totalPages ? pageFromUrl : currentPage;
  let t1;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t1 = page => buildHref(pathname, searchParams, page);
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const hrefForPage = t1;
  let t2;
  if ($[3] !== resolvedCurrentPage || $[4] !== totalPages) {
    t2 = () => {
      const pages = [];
      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        let startPage = Math.max(2, resolvedCurrentPage - 1);
        let endPage = Math.min(totalPages - 1, resolvedCurrentPage + 1);
        if (resolvedCurrentPage <= 3) {
          startPage = 2;
          endPage = 5;
        }
        if (resolvedCurrentPage >= totalPages - 2) {
          startPage = totalPages - 4;
          endPage = totalPages - 1;
        }
        if (startPage > 2) {
          pages.push("...");
        }
        for (let i_0 = startPage; i_0 <= endPage; i_0++) {
          pages.push(i_0);
        }
        if (endPage < totalPages - 1) {
          pages.push("...");
        }
        pages.push(totalPages);
      }
      return pages;
    };
    $[3] = resolvedCurrentPage;
    $[4] = totalPages;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const getPageNumbers = t2;
  let T0;
  let T1;
  let T2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[6] !== getPageNumbers || $[7] !== hrefForPage || $[8] !== resolvedCurrentPage) {
    const pageNumbers = getPageNumbers();
    T2 = PaginationRoot;
    t7 = "bg-pink-50 h-[58px] rounded-[100px] w-full max-w-[569px] flex items-center justify-center";
    T1 = PaginationContent;
    t5 = "flex gap-4 lg:gap-[39px] h-[38px] justify-between items-center w-full px-[10px]";
    if ($[17] !== hrefForPage || $[18] !== resolvedCurrentPage) {
      t6 = <PaginationItem>{resolvedCurrentPage === 1 ? <Button disabled={true} variant="default" size="icon" aria-label="Previous page"><ChevronLeft className="h-4 w-4" /></Button> : <Button asChild={true} variant="default" size="icon" aria-label="Previous page"><Link href={hrefForPage(resolvedCurrentPage - 1)} scroll={false}><ChevronLeft className="h-4 w-4" /></Link></Button>}</PaginationItem>;
      $[17] = hrefForPage;
      $[18] = resolvedCurrentPage;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    T0 = PaginationItem;
    t3 = "flex gap-2 md:gap-[22px] items-center text-[18px] text-center";
    t4 = pageNumbers.map((page_0, index) => {
      if (page_0 === "...") {
        return <span key={`ellipsis-${index}`} className="text-primary-blue">...</span>;
      }
      const pageNum = page_0 as number;
      const isActive = pageNum === resolvedCurrentPage;
      return <Link key={pageNum} href={hrefForPage(pageNum)} scroll={false} className={`leading-[26px] ${isActive ? "text-pink-100 bg-primary-blue rounded-full size-[38px] flex items-center justify-center" : "text-primary-blue hover:text-primary-blue/80"}`} aria-current={isActive ? "page" : undefined}>{pageNum}</Link>;
    });
    $[6] = getPageNumbers;
    $[7] = hrefForPage;
    $[8] = resolvedCurrentPage;
    $[9] = T0;
    $[10] = T1;
    $[11] = T2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    T0 = $[9];
    T1 = $[10];
    T2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
  }
  let t8;
  if ($[20] !== T0 || $[21] !== t3 || $[22] !== t4) {
    t8 = <T0 className={t3}>{t4}</T0>;
    $[20] = T0;
    $[21] = t3;
    $[22] = t4;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== hrefForPage || $[25] !== resolvedCurrentPage || $[26] !== totalPages) {
    t9 = <PaginationItem>{resolvedCurrentPage === totalPages ? <Button disabled={true} variant="default" size="icon" aria-label="Next page"><ChevronRight className="h-4 w-4" /></Button> : <Button asChild={true} variant="default" size="icon" aria-label="Next page"><Link href={hrefForPage(resolvedCurrentPage + 1)} scroll={false}><ChevronRight className="h-4 w-4" /></Link></Button>}</PaginationItem>;
    $[24] = hrefForPage;
    $[25] = resolvedCurrentPage;
    $[26] = totalPages;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== T1 || $[29] !== t5 || $[30] !== t6 || $[31] !== t8 || $[32] !== t9) {
    t10 = <T1 className={t5}>{t6}{t8}{t9}</T1>;
    $[28] = T1;
    $[29] = t5;
    $[30] = t6;
    $[31] = t8;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== T2 || $[35] !== t10 || $[36] !== t7) {
    t11 = <T2 className={t7}>{t10}</T2>;
    $[34] = T2;
    $[35] = t10;
    $[36] = t7;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  return t11;
}